import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { API } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";

const EMPTY = { parent_name: "", phone: "", course: "", timing: "", child_age: "", message: "" };
const cls = "w-full rounded-xl bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder:text-white/50 outline-none focus:ring-2 focus:ring-white/60 transition-shadow";

export default function EnquiryForm() {
  const { config, slug } = useConfig();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/sites/${slug}/enquiry`, form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your name and 10-digit mobile number");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white/10 rounded-3xl p-8 flex flex-col items-center justify-center text-center" data-testid="enquiry-success">
        <CheckCircle2 className="w-12 h-12 text-accent-brand mb-3" />
        <h3 className="text-2xl font-semibold">Thank you!</h3>
        <p className="text-white/75 mt-1">Our team will contact you shortly.</p>
        <button onClick={() => setDone(false)} className="mt-5 text-sm underline" data-testid="enquiry-another-button">Send another enquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white/10 rounded-3xl p-5 sm:p-7 space-y-3" data-testid="enquiry-form">
      <h3 className="text-xl font-semibold mb-1">Admission enquiry</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required minLength={2} className={cls} placeholder="Parent's name" value={form.parent_name} onChange={set("parent_name")} data-testid="enquiry-name-input" />
        <input required className={cls} placeholder="10-digit mobile number" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="enquiry-phone-input" />
        <select className={cls} value={form.course} onChange={set("course")} data-testid="enquiry-course-select">
          <option value="" className="text-stone-900">Interested class</option>
          {config.courses.map((c) => <option key={c.name} value={c.name} className="text-stone-900">{c.name}</option>)}
        </select>
        <select className={cls} value={form.timing} onChange={set("timing")} data-testid="enquiry-timing-select">
          <option value="" className="text-stone-900">Preferred timing</option>
          {config.timings.map((t) => <option key={t} value={t} className="text-stone-900">{t}</option>)}
        </select>
      </div>
      <input className={cls} placeholder="Child's age (optional)" value={form.child_age} onChange={set("child_age")} maxLength={40} data-testid="enquiry-age-input" />
      <textarea rows={3} className={cls} placeholder="Your question (optional)" value={form.message} onChange={set("message")} maxLength={1000} data-testid="enquiry-message-input" />
      <button type="submit" disabled={busy} className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-accent font-semibold disabled:opacity-60" data-testid="enquiry-submit-button">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send enquiry
      </button>
    </form>
  );
}
